import { AddressSourceSchema, ResolvedAddressSchema } from "./addresses";
import { profileIds, profileRequirements } from "./incidentProfiles";
import {
  CatalogReviewSchema,
  PublishedFleetSchema,
  PublishedVehicleSchema,
} from "./catalogEvidence";
import { z } from "zod";
import { services, vehicleKinds, vehicleDefinitions } from "./organizations";
import { defaultArea } from "./regions";
export const Service = z.enum(services);
export const VehicleKind = z.enum(vehicleKinds);
export const IncidentType = z.enum([
  "brand",
  "vu",
  "medizinisch",
  "polizei",
  "wasser",
  "berg",
  "unwetter",
]);
export const Point = z.tuple([
  z.number().min(-90).max(90),
  z.number().min(-180).max(180),
]);
export type Point = z.infer<typeof Point>;
export const AreaSchema = z.object({
  name: z.string().trim().min(2).max(80),
  center: Point,
  radiusKm: z.number().min(2).max(250),
  controlCenterId: z.string().min(3).max(160).optional(),
  federalState: z.string().min(2).max(50).optional(),
  jurisdictions: z.array(z.string().min(2).max(100)).max(20).optional(),
  boundary: z.array(z.array(Point).min(4).max(2500)).max(80).optional(),
  bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
  sourceUrl: z
    .string()
    .url()
    .regex(/^https:\/\//)
    .optional(),
});
export const SiteSchema = z.object({
  addressSource: AddressSourceSchema.optional(),
  street: z.string().max(140).optional(),
  houseNumber: z.string().max(30).optional(),
  postcode: z.string().max(12).optional(),
  address: z.string().trim().min(3).max(160),
  district: z.string().trim().min(2).max(100),
  location: Point,
});
export const SourceSchema = z.object({
  kind: z.enum(["osm", "manual", "simulation", "bos"]),
  id: z.string().max(80).optional(),
  url: z
    .string()
    .url()
    .max(250)
    .regex(/^https?:\/\//)
    .optional(),
  dataTimestamp: z.string().max(40).optional(),
});
export const StationSchema = z.object({
  id: z.string(),
  name: z.string().min(2).max(160),
  service: Service,
  location: Point,
  organization: z.string().max(160).optional(),
  stationType: z.string().max(100).optional(),
  source: SourceSchema.optional(),
  areaManaged: z.boolean().optional(),
  temporaryAid: z.boolean().optional(),
  level: z.number().int().min(1).max(5).default(1),
  review: CatalogReviewSchema.optional(),
  fleet: PublishedFleetSchema.optional(),
  catalogCustomized: z.boolean().optional(),
});
export type Station = z.infer<typeof StationSchema>;
export const VehicleSchema = z.object({
  id: z.string(),
  name: z.string().min(2).max(100),
  kind: VehicleKind,
  simulated: z.boolean().optional(),
  catalogManaged: z.boolean().optional(),
  policeBaseline: z.boolean().optional(),
  policeSupport: z.object({ preparationSeconds: z.number().int().min(0).max(600) }).optional(),
  catalogRetire: z.boolean().optional(),
  scheduleOffDuty: z.boolean().optional(),
  mutualAid: z
    .object({
      centerId: z.string(),
      centerName: z.string(),
      catalogId: z.string(),
      evidenceId: z.string(),
      requestedAt: z.number(),
      requestedIncidentId: z.string().optional(),
    })
    .optional(),
  evidence: PublishedVehicleSchema.optional(),
  stationId: z.string(),
  status: z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(6),
    z.literal(7),
    z.literal(8),
  ]),
  incidentId: z.string().nullable(),
  transport: z
    .object({
      patientId: z.string(),
      hospitalId: z.string(),
      handoverUntil: z.number().optional(),
    })
    .optional(),
  location: Point,
  journey: z
    .object({
      from: Point,
      to: Point,
      start: z.number(),
      end: z.number(),
      returning: z.boolean(),
      requestId: z.string().optional(),
      routeStatus: z.enum(["pending", "ready", "error"]).optional(),
      routeError: z.string().max(300).optional(),
      provider: z.enum(["osrm", "air", "test"]).optional(),
      geometry: z.array(Point).min(2).max(50000).optional(),
      segmentSeconds: z.array(z.number().finite().min(0)).max(49999).optional(),
      distanceKm: z.number().finite().min(0).optional(),
      departureAt: z.number().optional(),
      turnoutSeconds: z.number().min(0).max(600).optional(),
    })
    .nullable(),
});
export type Vehicle = z.infer<typeof VehicleSchema>;
export const EventSchema = z.object({
  id: z.string(),
  at: z.number(),
  text: z.string(),
  type: z.enum([
    "call",
    "dispatch",
    "status",
    "warning",
    "measure",
    "note",
    "system",
  ]),
});
export type TimelineEvent = z.infer<typeof EventSchema>;
export const PatientSchema = z.object({
  id: z.string(),
  label: z.string(),
  severity: z.enum(["stable", "critical"]),
  specialty: z.enum([
    "general",
    "trauma",
    "cardiac",
    "stroke",
    "burn",
    "pediatric",
  ]),
  phase: z.enum(["waiting", "transport", "handover", "delivered"]),
  careSeconds: z.number().min(0).default(0),
  vehicleId: z.string().optional(),
  hospitalId: z.string().optional(),
  deliveredAt: z.number().optional(),
});
export type Patient = z.infer<typeof PatientSchema>;
export const HospitalSchema = z.object({
  id: z.string(),
  name: z.string().max(200),
  location: Point,
  sourceUrl: z.string().url(),
  specialties: z.array(
    z.enum(["general", "trauma", "cardiac", "stroke", "burn", "pediatric"]),
  ),
  helipad: z.boolean(),
  emergencyConfirmed: z.boolean(),
});
export type Hospital = z.infer<typeof HospitalSchema>;
export const callQuestions = [
  "opening",
  "where",
  "who",
  "what",
  "how",
  "count",
  "danger",
] as const;
export const CallSchema = SiteSchema.extend({
  id: z.string(),
  profileId: z.enum(profileIds),
  callerName: z.string().max(100),
  speakerId: z.number().int().min(0).max(235),
  createdAt: z.number(),
  phase: z.enum(["ringing", "active", "ended"]),
  acceptedAt: z.number().optional(),
  endedAt: z.number().optional(),
  incidentId: z.string().optional(),
  answered: z.array(z.enum(callQuestions)).max(7),
  answers: z.record(z.enum(callQuestions), z.string().max(1500)),
});
export type EmergencyCall = z.infer<typeof CallSchema>;
export type CallQuestion = (typeof callQuestions)[number];
export const CallSettingsSchema = z.object({
  minSeconds: z.number().int().min(30).max(3600),
  maxSeconds: z.number().int().min(30).max(3600),
  maxWaiting: z.number().int().min(1).max(10),
  scaleWithFleet: z.boolean(),
});
export const IncidentSchema = z.object({
  id: z.string(),
  number: z.number(),
  type: IncidentType,
  title: z.string(),
  profileId: z.enum(profileIds).optional(),
  callId: z.string().optional(),
  actualProfileId: z.enum(profileIds).optional(),
  patients: z.array(PatientSchema).max(100).optional(),
  pendingReinforcement: z.boolean().optional(),
  sceneResolvedAt: z.number().optional(),
  nextEscalationAt: z.number().optional(),
  escalationRoll: z.number().min(0).max(1).optional(),
  firstArrivalAt: z.number().optional(),
  reconnaissanceAt: z.number().optional(),
  completionReportedAt: z.number().optional(),
  address: z.string(),
  district: z.string(),
  location: Point,
  addressSource: AddressSourceSchema.optional(),
  street: z.string().max(140).optional(),
  houseNumber: z.string().max(30).optional(),
  postcode: z.string().max(12).optional(),
  callerName: z.string().max(100).optional(),
  report: z.string(),
  priority: z.union([z.literal(1), z.literal(2)]),
  phase: z.enum([
    "gemeldet",
    "alarmiert",
    "vorort",
    "stabilisiert",
    "abgeschlossen",
  ]),
  createdAt: z.number(),
  escalation: z.number().int().min(0).max(2),
  workSeconds: z.number().min(0),
  lastRadioAt: z.number().optional(),
  nextRadioAt: z.number().optional(),
  radioSeed: z.number().int().nonnegative().optional(),
  actions: z.array(z.string()),
  timeline: z.array(EventSchema),
});
export type Incident = z.infer<typeof IncidentSchema>;
export const RadioMessageSchema = z.object({
  id: z.string().max(120),
  sequence: z.number().int().positive(),
  at: z.number(),
  kind: z.enum([
    "call",
    "alarm",
    "status",
    "request",
    "situation",
    "warning",
    "clear",
    "speechRequest",
    "conversation",
  ]),
  vehicleId: z.string().optional(),
  incidentId: z.string().optional(),
  status: z.number().int().min(1).max(8).optional(),
  transmissions: z
    .array(
      z.object({ sender: z.string().max(180), text: z.string().max(1500) }),
    )
    .min(1)
    .max(4),
});
export type RadioMessage = z.infer<typeof RadioMessageSchema>;
export const SpeechRequestSchema = z.object({
  id: z.string().max(150),
  vehicleId: z.string(),
  incidentId: z.string(),
  reason: z.enum(["reconnaissance", "reinforcement", "update", "completion"]),
  urgent: z.boolean(),
  createdAt: z.number(),
  phase: z.enum(["pending", "reported"]),
  report: z.string().max(1500).optional(),
  reportedAt: z.number().optional(),
});
export type SpeechRequest = z.infer<typeof SpeechRequestSchema>;
export const StateSchema = z.object({
  version: z.literal(1),
  revision: z.number().int().min(0),
  now: z.number(),
  nextNumber: z.number().int(),
  seed: z.number(),
  running: z.boolean(),
  speed: z.union([z.literal(1), z.literal(5), z.literal(20)]),
  autoGenerate: z.boolean(),
  nextCallAt: z.number(),
  calls: z.array(CallSchema).max(100).optional(),
  callSettings: CallSettingsSchema.optional(),
  hospitals: z.array(HospitalSchema).max(100).optional(),
  area: AreaSchema.default(defaultArea),
  verifiedFleetOnly: z.boolean().optional(),
  resourcesCatalogRevision: z.string().max(100).optional(),
  realAddresses: z.boolean().optional(),
  addressPool: z.array(ResolvedAddressSchema).max(120).optional(),
  addressError: z.string().max(300).optional(),
  stations: z.array(StationSchema).max(1200),
  vehicles: z.array(VehicleSchema).max(4000),
  incidents: z.array(IncidentSchema).max(500),
  radioMessages: z.array(RadioMessageSchema).max(300).default([]),
  nextRadioSequence: z.number().int().positive().default(1),
  speechRequests: z.array(SpeechRequestSchema).max(100).default([]),
});
export type SimState = z.infer<typeof StateSchema>;
export const CommandSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("generateCall"),
    profileId: z.enum(profileIds).optional(),
  }),
  z.object({ type: z.literal("acceptCall"), callId: z.string() }),
  z.object({
    type: z.literal("askCall"),
    callId: z.string(),
    question: z.enum(callQuestions),
  }),
  z.object({ type: z.literal("endCall"), callId: z.string() }),
  z.object({
    type: z.literal("incidentFromCall"),
    callId: z.string(),
    profileId: z.enum(profileIds),
    report: z.string().trim().min(5).max(1500),
  }),
  z.object({
    type: z.literal("transportPatient"),
    incidentId: z.string(),
    patientId: z.string(),
    vehicleId: z.string(),
    hospitalId: z.string(),
  }),
  z.object({ type: z.literal("setArea"), area: AreaSchema }),
  z.object({
    type: z.literal("equipStation"),
    stationId: z.string(),
    packageId: z.enum([
      "standard",
      "hvo",
      "sanitaet",
      "spezialpolizei",
      "fuehrung",
    ]),
  }),
  z.object({
    type: z.literal("dispatch"),
    incidentId: z.string(),
    vehicleIds: z.array(z.string()).min(1).max(30),
  }),
  z.object({
    type: z.literal("createIncident"),
    incidentType: IncidentType.optional(),
    profileId: z.enum(profileIds).optional(),
    locationId: z.number().int().min(0).max(7).optional(),
    site: SiteSchema.optional(),
    siteId: z.string().min(8).max(80).optional(),
    report: z.string().trim().min(5).max(1500).optional(),
  }),
  z.object({
    type: z.literal("note"),
    incidentId: z.string(),
    text: z.string().trim().min(1).max(1000),
  }),
  z.object({
    type: z.literal("measure"),
    incidentId: z.string(),
    action: z.enum([
      "Absicherung",
      "Menschenrettung",
      "Brandbekämpfung",
      "Patientenversorgung",
      "Erstversorgung",
      "Spezialkräfte einsetzen",
      "Technische Unterstützung",
      "Betreuung",
    ]),
  }),
  z.object({ type: z.literal("release"), vehicleId: z.string() }),
  z.object({
    type: z.literal("withdraw"),
    vehicleId: z.string(),
    incidentId: z.string(),
    mode: z.enum(["standby", "return"]),
  }),
  z.object({ type: z.literal("returnToStation"), vehicleId: z.string() }),
  z.object({
    type: z.literal("redirect"),
    vehicleId: z.string(),
    fromIncidentId: z.string(),
    incidentId: z.string(),
  }),
  z.object({ type: z.literal("grantSpeech"), requestId: z.string() }),
  z.object({ type: z.literal("acknowledgeSpeech"), requestId: z.string() }),
  z.object({ type: z.literal("requestSituation"), vehicleId: z.string() }),
  z.object({ type: z.literal("requestStatus"), vehicleId: z.string() }),
  z.object({ type: z.literal("close"), incidentId: z.string() }),
  z.object({
    type: z.literal("settings"),
    running: z.boolean().optional(),
    speed: z.union([z.literal(1), z.literal(5), z.literal(20)]).optional(),
    autoGenerate: z.boolean().optional(),
    callSettings: CallSettingsSchema.optional(),
  }),
  z.object({
    type: z.literal("vehicleStatus"),
    vehicleId: z.string(),
    status: z.union([z.literal(2), z.literal(6)]),
  }),
  z.object({
    type: z.literal("saveStation"),
    id: z.string().optional(),
    name: z.string().trim().min(2).max(160),
    service: Service,
    location: Point,
    organization: z.string().trim().max(160).optional(),
    stationType: z.string().trim().max(100).optional(),
  }),
  z.object({
    type: z.literal("saveVehicle"),
    id: z.string().optional(),
    name: z.string().trim().min(2).max(100),
    kind: VehicleKind,
    stationId: z.string(),
  }),
]);
export type Command = z.infer<typeof CommandSchema>;
export const statusLabels: Record<number, string> = {
  1: "Frei über Funk",
  2: "Frei auf Wache",
  3: "Auf Anfahrt",
  4: "An Einsatzstelle",
  6: "Außer Dienst",
  7: "Patient aufgenommen",
  8: "Am Transportziel",
};
export const phaseLabels: Record<Incident["phase"], string> = {
  gemeldet: "Neu eingegangen",
  alarmiert: "Kräfte auf Anfahrt",
  vorort: "In Bearbeitung",
  stabilisiert: "Lage stabilisiert",
  abgeschlossen: "Abgeschlossen",
};
export const kindService = Object.fromEntries(
  Object.entries(vehicleDefinitions).map(([kind, def]) => [
    kind,
    def.services[0],
  ]),
) as Record<Vehicle["kind"], Station["service"]>;
export const incidentLabels: Record<
  Incident["type"],
  { title: string; code: string; category: string }
> = {
  brand: { title: "Gebäudebrand", code: "B3", category: "BRAND" },
  vu: { title: "Verkehrsunfall", code: "TH3", category: "TECHNISCHE HILFE" },
  medizinisch: {
    title: "Medizinischer Notfall",
    code: "RD2",
    category: "RETTUNGSDIENST",
  },
  polizei: {
    title: "Besondere Polizeilage",
    code: "POL3",
    category: "POLIZEI",
  },
  wasser: { title: "Wasserrettung", code: "WR2", category: "WASSERRETTUNG" },
  berg: { title: "Bergrettung", code: "BR2", category: "BERGRETTUNG" },
  unwetter: {
    title: "Unwetterschaden",
    code: "TH2",
    category: "TECHNISCHE HILFE",
  },
};
export function requirements(
  i: Incident,
): Partial<Record<Vehicle["kind"], number>> {
  return profileRequirements(i);
}

export function fulfills(
  kind: Vehicle["kind"],
  needed: Vehicle["kind"],
  i: Incident,
) {
  return (
    kind === needed ||
    (needed === "HLF" &&
      ((i.type === "brand" && kind === "LF") ||
        (i.type === "vu" && kind === "RW"))) ||
    (needed === "LF" &&
      (kind === "HLF" ||
        kind === "TLF" ||
        (i.profileId === "brand-klein" && kind === "TSF"))) ||
    (needed === "HLF" &&
      i.type === "unwetter" &&
      (kind === "LF" || kind === "RW")) ||
    (needed === "RTW" && (kind === "RTH" || kind === "ITH")) ||
    (needed === "NEF" && (kind === "RTH" || kind === "ITH")) ||
    (needed === "FuStW" && kind === "BePo") ||
    (needed === "GW-Wasser" && kind === "RTB")
  );
}
export const measureKinds: Record<string, Vehicle["kind"][]> = {
  Absicherung: ["FuStW", "BePo", "HLF", "LF", "RW"],
  Menschenrettung: ["HLF", "RW", "DLK", "Bergrettung", "GW-Wasser", "RTB"],
  Brandbekämpfung: ["HLF", "LF", "TLF"],
  Patientenversorgung: ["RTW", "NEF", "RTH", "ITH"],
  Erstversorgung: ["HvO", "RTW", "NEF", "KTW", "RTH"],
  "Spezialkräfte einsetzen": ["SEK", "MEK"],
  "Technische Unterstützung": ["GKW", "MzGW", "RW", "HLF", "ELW"],
  Betreuung: ["SEG-Betreuung", "GW-San", "MTF"],
};
export function isOpen(i: Incident) {
  return i.phase !== "abgeschlossen";
}
